/**
 * Notifications panel (12-Shared-States/Drafts/05-notifications-panel-light-v1.png).
 * Per user and workspace: today's last free days (derived on read from vehicles the
 * user may see) and email send results. Refreshed when opened and every two minutes.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bell, Calendar, CircleAlert, CircleHelp, FileText, Lock, Mail, Settings, UserPlus } from '../ui/glyphs.tsx';
import { ageLabel, listNotifications, retryRead, markNotificationsRead, type Notice } from '@miraops/contracts';
import { transport } from '../lib/session.ts';
import { useI18n, type I18n } from '../lib/i18n.tsx';
import { Button, useDismiss } from '../ui/ui.tsx';

function age(i18n: I18n, at: number): string {
  const a = ageLabel(at);
  return a.unit === 'now' ? i18n.t('notice.justNow') : i18n.t(`notice.ago.${a.unit}`, { n: a.value });
}

export function Notifications({ workspaceId, workspaceName, onOpenDispatch, onSettings, onOpenSend }: { workspaceId: string; workspaceName: string; onOpenDispatch: () => void; onSettings: () => void; onOpenSend: (id: string) => void }) {
  const i18n = useI18n();
  const { t } = i18n;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notice[] | null>(null);
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const [failed, setFailed] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));

  const load = useCallback(() => {
    retryRead(() => listNotifications(transport, workspaceId)).then(list => { setItems(list); setFailed(false); }).catch(() => setFailed(true));
  }, [workspaceId]);
  useEffect(() => { load(); const id = setInterval(load, 120_000); return () => clearInterval(id); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const unread = (items ?? []).filter(n => !n.read);
  const alerting = unread.filter(n => !n.deferred);
  const shown = tab === 'unread' ? unread : items ?? [];
  const markRead = async (ids?: string[]) => {
    setItems(list => list?.map(n => (!ids || ids.includes(n.id) ? { ...n, read: true } : n)) ?? null);
    await markNotificationsRead(transport, workspaceId, ids).catch(() => load());
  };

  return (
    <div className="notice-wrap" ref={ref}>
      <button type="button" className="icon-btn" aria-label={unread.length ? t('notice.titleUnread', { n: unread.length }) : t('nav.notifications')}
        aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <Bell size={19} strokeWidth={1.7} />{alerting.length > 0 && <span className="notice-dot" aria-hidden />}
      </button>
      {open && (
        <div className="notice-panel" role="dialog" aria-label={t('nav.notifications')}>
          <header>
            <div><h2>{t('nav.notifications')}</h2><p className="muted">{workspaceName}</p></div>
            <div className="notice-head-actions">
              <button type="button" className="icon-btn" aria-label={t('notify.title')} onClick={() => { setOpen(false); onSettings(); }}><Settings size={18} /></button>
              <Button variant="link" disabled={!unread.length} onClick={() => void markRead()}>{t('notice.markAll')}</Button>
            </div>
          </header>
          <div className="order-tabs notice-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'all'} className={`tab${tab === 'all' ? ' on' : ''}`} onClick={() => setTab('all')}>{t('notice.all')}</button>
            <button type="button" role="tab" aria-selected={tab === 'unread'} className={`tab${tab === 'unread' ? ' on' : ''}`} onClick={() => setTab('unread')}>{t('notice.unread', { n: unread.length })}</button>
          </div>
          <ul>
            {failed && !items && <li className="muted pad">{t('notice.failed')}</li>}
            {items && shown.length === 0 && <li className="muted pad">{t(tab === 'unread' ? 'notice.noneUnread' : 'notice.none')}</li>}
            {shown.map(n => (
              <li key={n.id} className={n.read ? '' : 'unread'}>
                <span className="notice-mark" aria-hidden />
                <span className={`notice-ico kind-${n.kind}`}>{n.kind === 'lfd_today' || n.kind === 'storage_running' ? <AlertTriangle size={22} /> : n.kind === 'pickup_due' ? <Calendar size={22} /> : n.kind === 'evidence_missing' ? <FileText size={22} /> : n.kind === 'invitation_received' ? <UserPlus size={22} /> : n.kind === 'send_accepted' || n.kind === 'send_replied' ? <Mail size={22} /> : n.kind === 'send_failed' || n.kind === 'send_bounced' || n.kind === 'fetch_failed' ? <CircleAlert size={22} /> : n.kind === 'security_change' || n.kind === 'new_sign_in' ? <Lock size={22} /> : <CircleHelp size={22} />}</span>
                <div className="notice-text">
                  <strong>{t(`notice.${n.kind}`)}</strong>
                  <span className="muted">{n.kind === 'lfd_today' ? t('notice.lfdDetail', { n: n.quantity ?? 0 }) : n.kind === 'storage_running' ? t('notice.storageDetail', { n: n.quantity ?? 0 }) : n.kind === 'pickup_due' ? t('notice.pickupDetail', { n: n.quantity ?? 0 })
                    : n.kind === 'evidence_missing' ? t('notice.evidenceDetail', { n: n.quantity ?? 0 }) : n.kind === 'invitation_received' ? t('notice.invitationDetail', { name: n.label ?? '' })
                    : n.kind === 'handover_requested' ? t('notice.handoverRequestedDetail', { name: n.label ?? '' }) : n.kind === 'handover_answered' ? t('notice.handoverAnsweredDetail', { name: n.label ?? '' })
                    : n.kind === 'change_requested' ? t('notice.changeRequestedDetail', { ref: n.label ?? '' })
                    : n.kind === 'fetch_failed' ? t('notice.fetchDetail', { name: n.label ?? '' })
                    : n.kind === 'security_change' ? t('notice.securityDetail')
                    : n.kind === 'new_sign_in' ? t('notice.signInDetail', { name: n.label ?? '' })
                    : n.kind === 'send_bounced' ? t('notice.bounceDetail', { to: n.label ?? '' })
                    : n.kind === 'send_replied' ? t('notice.replyDetail', { from: n.label ?? '' })
                    : t('notice.sendDetail', { to: n.label ?? '', n: n.quantity ?? 0 })}{n.deferred ? ` · ${t('notice.quiet')}` : ''}</span>
                </div>
                <span className="muted small notice-age">{age(i18n, n.createdAt)}</span>
                {n.kind === 'lfd_today' || n.kind === 'storage_running' || n.kind === 'pickup_due' || n.kind === 'evidence_missing' || n.kind === 'handover_requested' || n.kind === 'handover_answered'
                  ? <Button size="sm" onClick={() => { void markRead([n.id]); setOpen(false); onOpenDispatch(); }}>{t('notice.viewToday')}</Button>
                  : n.targetKind === 'send_operation' && n.targetId ? <Button size="sm" onClick={() => { void markRead([n.id]); setOpen(false); onOpenSend(n.targetId!); }}>{t('notice.viewSend')}</Button>
                  : !n.read ? <Button size="sm" onClick={() => void markRead([n.id])}>{t('notice.markRead')}</Button> : <span />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
